/**
 * Mock Broker Client
 *
 * Implements the full BrokerClient interface with realistic demo data.
 * Used in demo mode (when VITE_DEMO_MODE=true) to provide:
 *   - Realistic portfolio positions (mix of equities, ETFs, bonds)
 *   - Account balance (₦5-50M)
 *   - Order placement that triggers WebSocket events
 *   - Position updates on fills
 *
 * Integrates with MockWsClient to emit order status and fill events.
 */

import { BrokerClient, type BalAction, BrokerApiError } from './brokerClient';
import { getMockPrice } from './mockMarketData';
import { MockWsClient, type FillCallback } from './mockWs';

/**
 * Deterministic seeded random for reproducible demo data
 */
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

/**
 * MockBrokerClient — thin wrapper conforming to BrokerClient interface.
 *
 * Returns hardcoded but realistic demo data. Integrates with MockWsClient
 * to emit order fills on placeOrder.
 */
export class MockBrokerClient {
  private ws?: MockWsClient;
  private mockAccountId = 'acc-mock-001';
  private mockBrokerId = 'mock-broker';

  // Mutable state for positions and balance (updated on trades)
  private _positions: any[] = [];
  private cashNaira: bigint = BigInt(25_000_000); // ₦25M starting

  constructor(wsOverride?: MockWsClient) {
    // Set up the fill callback before storing the reference
    const fillCallback: FillCallback = (clOrdId, symbol, side, quantity, fillPrice) => {
      this.updatePositionFromFill(symbol, side, quantity, fillPrice);
    };

    if (!wsOverride) {
      this.ws = new MockWsClient(fillCallback);
    } else {
      // If ws is provided, assume it's already configured
      this.ws = wsOverride;
    }

    this.initializePositions();
  }

  /**
   * Initialize realistic demo positions (equities, bonds, ETFs)
   */
  private initializePositions(): void {
    const equityPositions = [
      { symbol: 'ZENITHBANK', qty: 1000, avgCost: 32.00 },
      { symbol: 'DANGCEM', qty: 50, avgCost: 680.00 },
      { symbol: 'MTNN', qty: 200, avgCost: 285.00 },
      { symbol: 'GTCO', qty: 25, avgCost: 1150.00 },
      { symbol: 'NESTLE', qty: 100, avgCost: 950.00 },
      { symbol: 'UBA', qty: 500, avgCost: 40.00 },
    ];

    const bondPositions = [
      { symbol: 'FGN27-14.55', qty: 10, faceValue: 1_000_000, cleanPrice: 98.75 },
      { symbol: 'FGN30-15.10', qty: 5, faceValue: 1_000_000, cleanPrice: 97.20 },
      { symbol: 'FGN50-12.80', qty: 3, faceValue: 1_000_000, cleanPrice: 94.50 },
    ];

    const etfPositions = [
      { symbol: 'VETGRIF30', qty: 250, avgCost: 38.45 },
      { symbol: 'VANGOF40', qty: 180, avgCost: 41.80 },
      { symbol: 'NXTINDX', qty: 100, avgCost: 50.90 },
    ];

    // Build equity positions
    const equityRows = equityPositions.map((p) => {
      const currentPrice = getMockPrice(p.symbol);
      const marketValue = currentPrice * p.qty;
      const avgCostTotal = p.avgCost * p.qty;
      const unrealizedPnl = marketValue - avgCostTotal;

      return {
        accountId: this.mockAccountId,
        symbol: p.symbol,
        quantity: p.qty,
        averagePrice: p.avgCost.toString(),
        marketValue: marketValue.toFixed(2),
        unrealizedPnl: unrealizedPnl.toFixed(2),
        side: 'LONG',
        securityType: 'EQUITY',
      };
    });

    // Build bond positions (face value + clean price → dirty price → market value)
    const bondRows = bondPositions.map((b) => {
      const dirtyPrice = b.cleanPrice + 0.5; // approx accrued interest for display
      const marketValue = (dirtyPrice / 100) * b.qty * b.faceValue;
      const costValue = (100 / 100) * b.qty * b.faceValue;
      const unrealizedPnl = marketValue - costValue;

      return {
        accountId: this.mockAccountId,
        symbol: b.symbol,
        quantity: b.qty,
        averagePrice: b.cleanPrice.toString(),
        marketValue: marketValue.toFixed(2),
        unrealizedPnl: unrealizedPnl.toFixed(2),
        side: 'LONG',
        securityType: 'BOND',
        faceValueNaira: String(b.faceValue),
      };
    });

    // Build ETF positions
    const etfRows = etfPositions.map((e) => {
      const currentPrice = getMockPrice(e.symbol);
      const marketValue = currentPrice * e.qty;
      const avgCostTotal = e.avgCost * e.qty;
      const unrealizedPnl = marketValue - avgCostTotal;

      return {
        accountId: this.mockAccountId,
        symbol: e.symbol,
        quantity: e.qty,
        averagePrice: e.avgCost.toString(),
        marketValue: marketValue.toFixed(2),
        unrealizedPnl: unrealizedPnl.toFixed(2),
        side: 'LONG',
        securityType: 'ETF',
      };
    });

    this._positions = [...equityRows, ...bondRows, ...etfRows];
  }

  /**
   * Stub: link a broker account (real operation would happen server-side)
   */
  async link(brokerId: string, payload: Record<string, unknown>): Promise<any> {
    return { success: true, accountId: this.mockAccountId };
  }

  /**
   * Stub: unlink a broker account
   */
  async unlink(brokerId: string): Promise<any> {
    return { success: true };
  }

  /**
   * Return a single mock account
   */
  async accounts(brokerId: string): Promise<{ accounts: any[] }> {
    return {
      accounts: [
        {
          accountId: this.mockAccountId,
          brokerUserId: 'mock-user-001',
          displayName: 'Primary Trading Account',
          accountType: 'CASH',
          currency: 'NGN',
          status: 'ACTIVE',
        },
      ],
    };
  }

  /**
   * Return current positions with live mark prices
   */
  async positions(brokerId: string, accountId: string): Promise<{ positions: any[] }> {
    // Update market values with current prices
    const updated = this._positions.map((p) => {
      const currentPrice = getMockPrice(p.symbol);
      const marketValue = currentPrice * p.quantity;
      const avgCostTotal = Number(p.averagePrice) * p.quantity;
      const unrealizedPnl = marketValue - avgCostTotal;

      return {
        ...p,
        marketValue: marketValue.toFixed(2),
        unrealizedPnl: unrealizedPnl.toFixed(2),
      };
    });

    return { positions: updated };
  }

  /**
   * Return account balance summary
   */
  async balances(brokerId: string, accountId: string): Promise<{ balances: any[] }> {
    return {
      balances: [
        {
          accountId: this.mockAccountId,
          currency: 'NGN',
          cash: this.cashNaira.toString(),
          buyingPower: this.cashNaira.toString(),
          asOf: new Date().toISOString(),
        },
      ],
    };
  }

  /**
   * Place an order. Generates an order ID and triggers WS events via MockWsClient.
   */
  async placeOrder(brokerId: string, payload: Record<string, unknown>): Promise<any> {
    const clOrdId = String(payload.clOrdId ?? `CLO-${Math.random().toString(16).slice(2, 8)}`);
    const brokerOrderId = `BRO-${Math.random().toString(16).slice(2, 8).toUpperCase()}`;

    const symbol = String(payload.symbol ?? 'ZENITHBANK');
    const side = String(payload.side ?? 'BUY') as 'BUY' | 'SELL';
    const quantity = Number(payload.quantity ?? 100);
    const limitPrice = String(payload.limitPrice ?? getMockPrice(symbol).toFixed(2));

    // Validate basic constraints
    if (side !== 'BUY' && side !== 'SELL') {
      throw new BrokerApiError('validation_failed', 'Invalid side', 400);
    }

    if (quantity <= 0) {
      throw new BrokerApiError('validation_failed', 'Quantity must be positive', 400);
    }

    // For BUY orders, check if we have sufficient cash
    if (side === 'BUY') {
      const notional = BigInt(Math.floor(Number(limitPrice) * quantity * 100));
      if (notional > this.cashNaira) {
        throw new BrokerApiError('insufficient_funds', 'Insufficient buying power', 400);
      }
    }

    // Immediately deduct cash (optimistic execution)
    if (side === 'BUY') {
      const notional = BigInt(Math.floor(Number(limitPrice) * quantity * 100));
      this.cashNaira -= notional;
    }

    // Track this order with the WS client so it can emit fills
    if (this.ws) {
      this.ws.trackOrder(clOrdId, symbol, side, quantity, limitPrice, this.mockAccountId);
    }

    // Return immediately with order ID
    return {
      clOrdId,
      brokerOrderId,
      status: 'ACCEPTED',
      acceptedAt: new Date().toISOString(),
    };
  }

  /**
   * Stub: modify an order (not implemented in this demo)
   */
  async modifyOrder(brokerId: string, payload: Record<string, unknown>): Promise<any> {
    throw new BrokerApiError('invalid_action', 'Order modification not available in demo', 400);
  }

  /**
   * Stub: cancel an order
   */
  async cancelOrder(brokerId: string, payload: Record<string, unknown>): Promise<any> {
    return { success: true, clOrdId: payload.clOrdId };
  }

  /**
   * Ping for health check
   */
  async ping(brokerId: string): Promise<{ ok: boolean }> {
    return { ok: true };
  }

  /**
   * Get the internal WsClient instance (for wiring to Terminal)
   */
  getWsClient(): MockWsClient | undefined {
    return this.ws;
  }

  /**
   * Internal: update positions from a fill event (called by WS event handlers)
   */
  updatePositionFromFill(symbol: string, side: 'BUY' | 'SELL', quantity: number, fillPrice: number): void {
    const existing = this._positions.find((p) => p.symbol === symbol);

    if (existing) {
      const oldCost = Number(existing.averagePrice) * existing.quantity;
      if (side === 'BUY') {
        const newQty = existing.quantity + quantity;
        const newCost = (oldCost + fillPrice * quantity) / newQty;
        existing.quantity = newQty;
        existing.averagePrice = newCost.toString();
      } else {
        existing.quantity -= quantity;
        // Keep average cost (don't recalculate on SELL)
      }
    } else if (side === 'BUY') {
      // New position
      this._positions.push({
        accountId: this.mockAccountId,
        symbol,
        quantity,
        averagePrice: fillPrice.toString(),
        marketValue: (fillPrice * quantity).toFixed(2),
        unrealizedPnl: '0',
        side: 'LONG',
      });
    }

    // Update cash (add back notional if SELL, already deducted if BUY)
    if (side === 'SELL') {
      const notional = BigInt(Math.floor(fillPrice * quantity * 100));
      this.cashNaira += notional;
    }
  }
}
